import { Buffer } from 'buffer';

const PREFIX = 'greenbow.api.';
/**
 * Saves access and refresh tokens to localStorage using a defined prefix.
 *
 * @param {Object} params An object containing both the accessToken and refreshToken.
 * @param {string} params.accessToken The access token to be saved.
 * @param {string} params.refreshToken The refresh token to be saved.
 * @throws {Error} If the tokens are not valid strings.
 */
export function saveTokens({
  accessToken,
  refreshToken
}: {
  accessToken: string;
  refreshToken: string;
}) {
  const { captureException } = useSentry();

  try {
    // Basic validation for tokens
    if (
      !accessToken ||
      typeof accessToken !== 'string' ||
      !refreshToken ||
      typeof refreshToken !== 'string'
    ) {
      throw new Error('Invalid token values provided');
    }
    const authStore = useAuthStore(); // Access the store
    const storage = useStorage();

    // Save to local storage or other persistent storage
    storage.setItem(PREFIX + 'accessToken', accessToken);
    storage.setItem(PREFIX + 'refreshToken', refreshToken);

    // Update store's reactive state
    authStore.$patch({
      accessToken,
      refreshToken
    });
  } catch (error: any) {
    captureException(error, {
      context: 'Token Storage',
      accessTokenLength: accessToken?.length,
      refreshTokenLength: refreshToken?.length
    });
    throw new Error('Failed to save tokens. Error: ' + error.message);
  }
}

/**
 * Decodes a JWT token to retrieve its payload as a JSON object.
 *
 * @param {string} token The JWT token string to be decoded.
 * @returns {Object} The decoded JSON payload of the token.
 * @throws {Error} If the token format is invalid or essential fields are missing.
 */
export function decodeToken(token: string) {
  const { captureException } = useSentry();

  try {
    // Check if the token seems to be in the correct format
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid token format');
    }

    const base64UrlEncodedData = parts[1];
    const base64EncodedData = base64UrlEncodedData
      .replaceAll('-', '+')
      .replaceAll('_', '/');
    const decodedDataJSON = Buffer.from(base64EncodedData, 'base64').toString();

    // Parse the JSON string safely
    const parsedData = JSON.parse(decodedDataJSON);

    if (!parsedData.exp || !parsedData.iat) {
      throw new Error('Token is missing required fields');
    }

    return parsedData;
  } catch (error: any) {
    captureException(error, {
      context: 'Token Decoding',
      tokenLength: token?.length,
      tokenParts: token?.split('.').length
    });
    throw new Error('Failed to decode the token.');
  }
} // Ensure correct path
